import React, { useEffect, useRef, useState, useCallback } from 'react';
import useSignInThrottle from '../hooks/useSignInThrottle';
import { track } from '../utils/authTelemetry';
import { isInAppBrowser } from '../utils/inAppBrowser';
import InAppBrowserNotice from './InAppBrowserNotice';

export default function AuthModal({ isOpen, onClose, onSuccess }) {
  const loginContainerRef = useRef(null);
  const modalRef = useRef(null);
  const [error, setError] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  // Pre-empt Zoho's OAuth throttle: block repeated sign-in initiations (across reloads
  // and tabs) before the user trips Zoho's 10/10min account lockout. Polls while open.
  const throttle = useSignInThrottle(isOpen);

  void onSuccess;

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setIsVisible(true));
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setIsVisible(false);
    setTimeout(() => {
      setIsExiting(false);
      onClose();
    }, 200);
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) handleClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (isOpen && modalRef.current) modalRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    // Only initialise (which renders the Zoho widget → authorize request) when not
    // throttled. recordAttempt counts this initiation toward the rolling window.
    if (isOpen && !isInitialized && !throttle.blocked) {
      throttle.recordAttempt();
      track('SignInInitiated'); // ≈ one authorize round-trip begins
      initializeCatalystAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isInitialized, throttle.blocked]);

  // ── LOCAL DEV ONLY: embedded providers-only sign-in ─────────────────────
  // The hosted login page (below) cannot complete against http://localhost:*.
  // Its email/password form POSTs /accounts/p/<zaid>/webclient/v1/captcha,
  // which 400s locally, and Zoho then refuses the clientID interactively
  // ("Restricted Client — You do not have enough privilege to access this
  // clientID"). The federated "Sign in using Zoho" grant is a different path
  // and does work from localhost — this is how the sibling JAR app signs in
  // (js/index.js → catalyst.auth.signIn with signin_providers_only).
  //
  // Deliberately NO css_url: omitting it keeps Catalyst's default
  // embedded_signin_providers_only.css, which carries the display:none rules
  // that hide MFA / password-reset / recovery. Passing a css_url REPLACES that
  // file wholesale and un-hides those broken flows — see AUTH.md Rule 1.
  //
  // Production is untouched: it keeps the hosted redirect proven in ADR-037.
  const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);

  const renderEmbeddedSignIn = (attempt = 0) => {
    const signIn = window.catalyst?.auth?.signIn;
    if (typeof signIn !== 'function') {
      // Web SDK still loading — retry briefly, then fall back to hosted.
      if (attempt < 40) return void setTimeout(() => renderEmbeddedSignIn(attempt + 1), 100);
      window.location.href = `${window.location.origin}/__catalyst/auth/login`;
      return;
    }
    const returnTo = `${window.location.origin}/app/`;
    signIn.call(window.catalyst.auth, 'catalyst-login-container', {
      signin_providers_only: true,
      login_redirect: returnTo,
      service_url: returnTo,
    });
    setIsInitialized(true);

    // Catalyst injects the iframe at its own natural height, which overflows the
    // modal card and overlaps the page behind it. Sizing before the provider
    // button exists confuses the widget's own layout, so wait for the iframe and
    // then pin it. Same shim as JAR's fitLoginBox().
    let tries = 0;
    const fit = setInterval(() => {
      const frame = document.querySelector('#catalyst-login-container iframe');
      if (frame) {
        frame.style.width = '100%';
        frame.style.height = '96px';
        frame.style.border = 'none';
        clearInterval(fit);
      } else if (++tries > 40) {
        clearInterval(fit);
      }
    }, 100);
  };

  const initializeCatalystAuth = () => {
    if (isLocalhost) return renderEmbeddedSignIn();

    // ── HOSTED auth, not embedded ───────────────────────────────────────────
    // We redirect the browser to Catalyst's hosted login page instead of
    // rendering the embedded sign-in iframe (catalyst.auth.signIn(elementId,…)).
    //
    // Why: the embedded flow is broken for this project on EVERY v4 SDK version
    // (4.5.0, 4.5.2, 4.6.2). The client-side SDK builds
    // accounts.zohoportal.in/clientoauth/v2/<zaid>/remote/auth with
    // scope=undefined&client_id=undefined&jwt_token=undefined and gets a 500
    // (surfaced as a CORS error). Catalyst's hosted login page resolves the
    // scope server-side and works. After login, Catalyst returns the user to
    // `login_redirect` in client-package.json. See AUTH.md.
    window.location.href = `${window.location.origin}/__catalyst/auth/login`;
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setError('');
      setIsInitialized(false);
    }
  }, [isOpen]);

  if (!isOpen && !isExiting) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-200 ease-out ${isVisible ? 'backdrop-blur-sm' : ''}`}
      style={{ background: isVisible ? 'var(--bg-overlay)' : 'transparent' }}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className={`relative w-full max-w-md transform transition-all duration-200 ease-out ${isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'}`}
      >
        {/* Card */}
        <div
          className="relative rounded-2xl overflow-hidden"
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-accent)' }}
        >
          {/* Amber accent bar */}
          <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg, transparent, var(--accent), transparent)' }} />

          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full transition-colors z-10"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--accent-dim)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
            aria-label="Close sign-in modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="px-8 pt-10 pb-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h2 id="auth-modal-title" className="font-display text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                Larynx
              </h2>
              <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Sign in to continue</p>
            </div>

            {/* In-app browsers (WhatsApp/Cliq WebViews) loop on Zoho sign-in — guide out. */}
            {isInAppBrowser() && <InAppBrowserNotice />}

            {/* Error */}
            {error && (
              <div
                className="mb-6 px-4 py-3 rounded-xl flex items-start gap-3 text-sm"
                style={{ color: 'var(--color-danger)', background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)' }}
              >
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>{error}</p>
              </div>
            )}

            {/* Catalyst login container */}
            <div className="space-y-4">
              {throttle.blocked ? (
                <div className="w-full text-center py-4">
                  <p className="text-3xl mb-2">⏳</p>
                  <p className="text-sm font-semibold mb-1" style={{ color: 'var(--color-warning)' }}>
                    {throttle.reason === 'debounce' ? 'One moment…' : 'Just a quick pause'}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    {throttle.reason === 'debounce'
                      ? 'Preparing sign-in — please wait a moment.'
                      : `A few quick sign-in attempts in a row — pausing ${throttle.secondsRemaining}s so your Zoho account doesn't get rate-limited. You can try again right after.`}
                  </p>
                </div>
              ) : (
              <div
                id="catalyst-login-container"
                ref={loginContainerRef}
                className="min-h-[80px] flex items-center justify-center"
              >
                {!isInitialized && (
                  <div className="flex flex-col items-center gap-3 py-4">
                    <div
                      className="w-9 h-9 rounded-full"
                      style={{
                        border: '3px solid var(--border-accent)',
                        borderTopColor: 'var(--accent)',
                        animation: 'spin 0.8s linear infinite',
                      }}
                    />
                    <span className="text-sm" style={{ color: 'var(--text-faint)' }}>Redirecting to sign-in…</span>
                  </div>
                )}
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
